import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DependencyChart = ({ azureData, auth0Data, summary }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'text-green-500';
      case 'degraded':
        return 'text-orange-500';
      case 'unhealthy':
        return 'text-error';
      default:
        return 'text-gray-500';
    }
  };

  // Merge data by day
  const mergedData = [];
  
  // Process Azure Blob data
  if (azureData && azureData.length > 0) {
    azureData.forEach(item => {
      const existingDay = mergedData.find(d => d.day === item.day);
      if (existingDay) {
        existingDay.azureCount = item.count || 0;
      } else {
        mergedData.push({
          day: item.day,
          azureCount: item.count || 0,
          auth0Count: 0
        });
      }
    });
  }
  
  // Process Auth0 data
  if (auth0Data && auth0Data.length > 0) {
    auth0Data.forEach(item => {
      const existingDay = mergedData.find(d => d.day === item.day);
      if (existingDay) {
        existingDay.auth0Count = item.count || 0;
      } else {
        mergedData.push({
          day: item.day,
          azureCount: 0,
          auth0Count: item.count || 0
        });
      }
    });
  }
  
  // Sort by day
  mergedData.sort((a, b) => new Date(a.day) - new Date(b.day));

  // Calculate totals for display
  const totalAzureFailures = mergedData.reduce((sum, item) => sum + (item.azureCount || 0), 0) || 0;
  const totalAuth0Failures = mergedData.reduce((sum, item) => sum + (item.auth0Count || 0), 0) || 0;

  return (
    <div className="flex flex-col card-container rounded-md p-2 sm:p-3 w-full h-full">
      <div className="flex flex-col md:flex-row gap-2 md:gap-0 justify-between items-start md:items-center mb-2">
        <div className="text-lg font-semibold text-font-primary">Dependencies Health</div>
        <div className="flex gap-2 items-center text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#0078d4' }}></div>
            <span>Azure Blob (Y-Axis Left)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#eb5424' }}></div>
            <span>Auth0 (Y-Axis Right)</span>
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center justify-between mb-4">
        <div className="text-sm text-font-secondary">Azure Failures: <span className="font-medium text-blue-500">{totalAzureFailures}</span></div>
        <div className="text-sm text-font-secondary">Auth0 Failures: <span className="font-medium text-orange-500">{totalAuth0Failures}</span></div>
      </div>
      <div className="h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={mergedData} margin={{ top: 10, right: -40, left: -40, bottom: -5 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis 
              dataKey="day" 
              tickFormatter={formatDate} 
              stroke="#aaa"
              tick={{fontSize: 16}}
            />
            <YAxis 
              yAxisId="left" 
              orientation="left" 
              stroke="#0078d4"
              allowDecimals={false}
              tick={{fontSize: 16}}
            />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              stroke="#eb5424"
              allowDecimals={false}
              tick={{fontSize: 16}}
            />
            <Tooltip 
              formatter={(value, name) => {
                if (name === "azureCount") return [value, "Azure Blob Failures"];
                if (name === "auth0Count") return [value, "Auth0 Failures"];
                return [value, name];
              }}
              labelFormatter={formatDate}
            />
            <Bar 
              yAxisId="left"
              dataKey="azureCount" 
              name="Azure Blob Failures" 
              fill="#0078d4" 
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              yAxisId="right"
              dataKey="auth0Count" 
              name="Auth0 Failures" 
              fill="#eb5424" 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DependencyChart;